import ContentPanel from '../../display/ui/ContentPanel';
import ScreenContainer from '../../display/ui/ScreenContainer';
import { FONT_TITLE } from '../../constants/AppConstants';
import Hud from '../hud/Hud';
import { GetDefaultButtonCostume } from '../hud/HudConstants';
import TexturedPanel from '../../display/ui/TexturedPanel';

/**
 * Displays an image in an overlay container
 * @param {Phaser.Game} game - Phaser game state
 * @param {string} imagekey - key of image to display, if a url is provided this will take priority
 * @param {string} imageurl - Url of image to display ( absolute or relative ), if a key is provided that will take priority
 */
export default class ScreenOverlayStickerEarned extends ScreenContainer {
  constructor(game, { locationid, suitcase }) {
    super(game, { background: 'background_alt_overlay', modal: true });

    this.locationid = locationid;
    this.suitcase = suitcase;

    this.sticker = this.game.make.sprite(0, 0, 'locationstickers', locationid.toLowerCase());
    this.sticker.x = this.game.defaultWidth * 0.5 - this.sticker.width * 0.5;
    this.sticker.y = this.game.defaultHeight * 0.35 - this.sticker.height * 0.5;
    this.stickerShadow = this.game.make.sprite(0, 0, 'locationstickers', locationid.toLowerCase());
    this.stickerShadow.y = this.sticker.y + 8;
    this.stickerShadow.x = this.sticker.x;
    this.stickerShadow.alpha = 0.25;
    this.stickerShadow.tint = 0x000000;

    // create messaging
    this.messageUpper = this.game.make.text(0, 0, this.game.copy.getCopy('newsticker_1'), {
      font: '35px ' + this.game.titleFontStyle,
      fill: '#0090C1',
    });
    this.messageLower = this.game.make.text(0, 0, this.game.copy.getCopy('newsticker_2'), {
      font: '45px ' + this.game.titleFontStyle,
      fill: '#0090C1',
    });

    this.panel = new TexturedPanel(this.game, {
      width: Math.max(this.messageLower.width, this.messageUpper.width) + 150,
      height: this.messageLower.height + this.messageUpper.height + 60,
      corner: 30,
    });

    this.messageUpper.x = this.panel.width * 0.5 - this.messageUpper.width * 0.5;
    this.messageLower.x = this.panel.width * 0.5 - this.messageLower.width * 0.5;
    this.messageUpper.y = 30;
    this.messageLower.y = this.messageUpper.y + this.messageUpper.height;

    this.panel.x = this.game.defaultWidth * 0.5 - this.panel.width * 0.5;
    this.panel.y = this.sticker.y + this.sticker.height * 0.95;

    this.panel.add(this.messageUpper);
    this.panel.add(this.messageLower);
    this.add(this.panel);

    this.add(this.stickerShadow);
    this.add(this.sticker);

    this.btnClose = Hud.CreateHudButton(GetDefaultButtonCostume('rightarrow_med'), this.game);
    this.btnClose.anchor.set(0.5);
    this.btnClose.scale.set(0.8);
    this.btnClose.x = this.panel.x + this.panel.width;
    this.btnClose.y = this.panel.y + this.panel.height;
    this.btnClose.onInputDown.add(this.handleTweenSticker, this);
    this.add(this.btnClose);

    // dispatch when user closes screen, will auto hide itself
    this.onAnimationComplete = new Phaser.Signal();
  }

  handleTweenSticker() {
    // get sticker target location on suitcase
    const suitcaseStickerPosition = this.game.ui_config.ui.suitcase.sticker_position[
      this.locationid
    ];

    const stickerWorldX = this.sticker.worldPosition.x;
    const stickerWorldY = this.sticker.worldPosition.y;

    this.sticker.x = stickerWorldX;
    this.sticker.y = stickerWorldY;
    this.stickerShadow.alpha = 0;
    this.game.add.existing(this.sticker);
    this.stickerTween = this.game.add.tween(this.sticker).to(
      {
        x: this.suitcase.worldPosition.x + suitcaseStickerPosition.x,
        y: this.suitcase.worldPosition.y + suitcaseStickerPosition.y,
      },
      500,
      Phaser.Easing.Quadratic.Out,
      true
    );
    this.game.add.tween(this.sticker.scale).to(
      {
        x: 0.25,
        y: 0.25,
      },
      500,
      Phaser.Easing.Quadratic.Out,
      true
    );

    this.visible = false;

    this.stickerTween.onComplete.add(this.handleStickerComplete, this);
  }

  /*
  =======================================================================================================
    LOAD IMAGE
  =======================================================================================================
  */

  loadImage(imageurl) {
    this.imageurl = imageurl;

    // reset loader and load image
    this.game.load.baseURL = '';
    this.game.load.crossOrigin = 'anonymous';
    this.game.load.image(imageurl, imageurl, true);
    this.game.load.onFileComplete.add(this.handleImageLoaded, this);
    this.game.load.start();
  }

  handleImageLoaded() {
    // kill listener
    this.game.load.onFileComplete.remove(this.handleImageLoaded, this);
    // display the loaded image
    this.displayImage(this.imageurl);
  }

  /*
  =======================================================================================================
    DISPLAY IMAGE
  =======================================================================================================
  */

  displayImage(key) {
    if (this.image) {
      this.image.destroy(true);
    }
    // create image sprite
    this.image = this.game.make.image(0, 0, key);
    // figure out best scale for displaying the image
    const targetScale = Math.min(1, Math.min(911 / this.image.width, 550 / this.image.height));

    const targetHeight = this.image.height * targetScale;
    const targetWidth = this.image.width * targetScale;
    this.contentPanel.setSize(targetWidth, targetHeight);
    this.contentPanel.x = this.game.defaultWidth * 0.5 - this.contentPanel.width * 0.5;
    this.contentPanel.y = this.game.defaultHeight * 0.5 - this.contentPanel.height * 0.5;
    // scale and add the image
    this.image.scale.set(targetScale);
    this.contentPanel.addContent(this.image, true);
  }

  handleStickerComplete() {
    this.onAnimationComplete.dispatch();
    this.destroy(true);
  }

  /*
  =======================================================================================================
    CLOSE
  =======================================================================================================
  */

  closePanel() {
    this.visible = false;
    this.onScreenClosed.dispatch(this);
  }

  /*
  =======================================================================================================
    DESTROY
  =======================================================================================================
  */

  destroy() {
    this.onAnimationComplete.removeAll();
    this.onAnimationComplete = null;
    this.stickerShadow.destroy(true);
    this.stickerShadow = null;
    this.sticker.destroy(true);
    this.sticker = null;
    super.destroy(true);
  }
}



// WEBPACK FOOTER //
// ./js/app/ui/overlays/ScreenOverlayStickerEarned.js